import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Offcanvas, OffcanvasHeader } from "reactstrap";
import { Button, Badge } from "antd";
import styled from "styled-components";
import MoreButton from "Components/NavBar/NavbarMenus/MoreButton";
import BellIconButton from "Components/NavBar/NavbarMenus/BellIconButton";
import useMoreButton from "Components/NavBar/hooks/useMoreButton";
import FilterIcon from "Assets/Images/filter-white.svg";
import SearchWIcon from "Assets/Icons/SearchWIcon";
import LeftArrowWhite from "Assets/Icons/LeftArrowWhite";
import MultiSearchBar from "utilFunctions/shared/MultiSearchBar";
import { UserInfoContext } from "Context/CommonContext";
import { useAuth } from "utilFunctions/CustomHooks/useAuth";
import useResponsive from "Context/useResponsive";

const FilterBadge = styled(Badge)`
  .ant-badge-count {
    font-size: 10px;
    min-width: 16px;
    height: 16px;
    line-height: 16px;
    padding: 0 4px;
  }
`;

export const iconBoxStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 38,
  height: 38,
  borderRadius: 10,
  background: "rgba(255,255,255,0.18)",
  backdropFilter: "blur(6px)",
  cursor: "pointer",
  border: "none",
  outline: "none",
  transition: "background 0.18s",
};

interface NavbarMenuProps {
  showMobileSearch?: boolean;
}

const NavbarMenu: React.FC<NavbarMenuProps> = ({
  showMobileSearch = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const userInfo = React.useContext<any>(UserInfoContext);
  const { logout } = useAuth();
  const { isMobile } = useResponsive();
  const { checkPathWithIdOrNot, handleSearchIcon } = useMoreButton();

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [activeFilterCount, setActiveFilterCount] = useState(0);

  const onRequestClose = () => {
    setIsSearchOpen((previous) => !previous);
  };

  const routeToMyProfile = () => {
    navigate("/admin/faculties/" + userInfo?.id, {
      replace: false,
      state: {
        faculty: userInfo,
        selectedDefaultTab: "2",
      },
    });
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    logout();
  };

  useEffect(() => {
    const handleFilterCountChange = (event: Event) => {
      const customEvent = event as CustomEvent<{
        activeFilterCount?: number;
      }>;

      setActiveFilterCount(customEvent.detail?.activeFilterCount || 0);
    };

    window.addEventListener(
      "scheduleFilterCountChanged",
      handleFilterCountChange
    );

    return () => {
      window.removeEventListener(
        "scheduleFilterCountChanged",
        handleFilterCountChange
      );
    };
  }, []);

  useEffect(() => {
    if (location.pathname !== "/admin/schedule-beta") {
      setActiveFilterCount(0);
    }
  }, [location.pathname]);

  const shouldShowFilter =
    [
      "/admin/branch",
      "/admin/myBranch",
      "/admin/syllabusCompliances",
      "/admin/faculties",
      "/admin/schedule",
      "/admin/schedule-beta",
    ].includes(location.pathname) ||
    checkPathWithIdOrNot(location.pathname.split("/admin/")[1]);

  return (
    <>
      {showMobileSearch && (
        <Offcanvas
          backdrop={true}
          className="w-100 filterSlider"
          isOpen={isSearchOpen}
          direction="end"
          toggle={onRequestClose}
        >
          <OffcanvasHeader className="filterHeader w-100" />

          <div className="globalSearchFilterDivMB">
            <LeftArrowWhite onClick={onRequestClose} />
            <MultiSearchBar onRequestClose={onRequestClose} />
          </div>
        </Offcanvas>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          position: "relative",
        }}
      >
        {showMobileSearch && (
          <div
            onClick={() => setIsSearchOpen(true)}
            style={{ cursor: "pointer" }}
          >
            <SearchWIcon />
          </div>
        )}

        <BellIconButton />

        {shouldShowFilter && isMobile && (
          <FilterBadge
            count={activeFilterCount}
            overflowCount={99}
            size="small"
            offset={[-2, 2]}
          >
            <Button
              onClick={() => handleSearchIcon(location.pathname)}
              style={iconBoxStyle}
              icon={
                <img
                  src={FilterIcon}
                  alt="Filter"
                  style={{
                    width: "16px",
                    height: "16px",
                  }}
                />
              }
            />
          </FilterBadge>
        )}

        <MoreButton onProfileClick={routeToMyProfile} onLogout={handleLogout} />
      </div>
    </>
  );
};

export default NavbarMenu;
